import { getWindow } from './windowUtils'

/**
 * Scrolls the window to the top of a widget
 * @param element - the element to scroll to
 * @param shift - additional offset from the top (optional)
 */
export const scrollToTopOfWidget = (element: Element, shift: number) => {
  getWindow()?.scrollTo({
    top: element?.getBoundingClientRect().top + getWindow()?.scrollY - shift,
    behavior: 'smooth',
  })
}

/**
 * Scrolls an element into view within a scrollable container
 * @param element - the element to scroll into view
 * @param container - the scrollable container that holds the element
 * @param offset - offset from the top of the container (optional)
 * @param blockPosition - position of the element relative to the container (start, center, end, nearest)
 */
export const scrollElementIntoContainer = (
  element: Element,
  container?: Element,
  offset = 0,
  blockPosition: ScrollLogicalPosition = 'start'
) => {
  if (!element || !container) return

  // Check if container is an HTMLElement (to access scrollTop property)
  if (container instanceof HTMLElement) {
    // Calculate element position relative to the container
    const containerRect = container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()

    // Relative position of the element within the container
    const relativeTop =
      elementRect.top - containerRect.top + container?.scrollTop

    let targetScrollTop = 0

    // Determine new scroll position based on blockPosition
    switch (blockPosition) {
      case 'start':
        targetScrollTop = relativeTop - offset
        break
      case 'center':
        targetScrollTop =
          relativeTop - containerRect.height / 2 + elementRect.height / 2
        break
      case 'end':
        targetScrollTop =
          relativeTop + elementRect.height - containerRect.height + offset
        break
      case 'nearest':
      default:
        // If element is already visible, don't scroll
        // eslint-disable-next-line no-case-declarations
        const isVisible =
          relativeTop >= container.scrollTop &&
          relativeTop + elementRect.height <=
            container.scrollTop + containerRect.height

        if (isVisible) return

        // If element is above the visible area, scroll to the top
        if (relativeTop < container.scrollTop) {
          targetScrollTop = relativeTop - offset
        }
        // If element is below the visible area, scroll to the bottom
        else {
          targetScrollTop =
            relativeTop + elementRect.height - containerRect.height + offset
        }
        break
    }

    // Perform smooth scrolling
    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    })
  } else {
    // Fallback if container is not an HTMLElement
    element.scrollIntoView({
      behavior: 'smooth',
      block: blockPosition,
      inline: 'nearest',
    })
  }
}
